import React, { useCallback, useEffect, useState, useRef } from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { isEmpty } from 'validate.js';
import { v4 as uuidv4 } from 'uuid';

import PaymentRequestAmountQuestionV2 from 'dpl/breeder_dashboard/components/PaymentRequestAmountQuestionV2';
import {
  createPaymentProperties,
  trackPaymentEvent
} from 'dpl/breeder_dashboard/payments/utils/analytics';
import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import LoadingSpinner from 'dpl/components/LoadingSpinner';
import LoadingWrapper from 'dpl/components/LoadingWrapper';
import SmartImage from 'dpl/components/SmartImage';
import { CompletePaymentRequestFormV3 } from 'dpl/components/StripeCharge/New/StripeChargeModal/CompletePaymentRequestFormV3';
import {
  PAYMENT_TIER_TYPE,
  SUBMIT_PAYMENT_LABEL
} from 'dpl/components/StripeCharge/New/StripeChargeModal/constants';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_USER_JOURNEYS,
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEW_FEATURE_LOCATIONS
} from 'dpl/constants/analytics';
import {
  PAYMENT_REQUEST_AMOUNT,
  PAYMENT_REQUEST_CATEGORY
} from 'dpl/constants/query_params';
import StripeChargeModalAccountDataStepContainer from 'dpl/containers/StripeCharge/New/StripeChargeModalAccountDataStepContainer';
import { setupIntent as createSetupIntentMutationDefinition } from 'dpl/payment_methods/resourceDefinitions';
import { PAYMENT_REQUEST_CATEGORIES } from 'dpl/shared/constants/breeder_app';
import useResourceMutation from 'dpl/shared/fetching/hooks/useResourceMutation';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import useForm from 'dpl/shared/form/hooks/useForm';
import FormContext from 'dpl/shared/form/utils/context';
import { debounce, scrollTo } from 'dpl/shared/utils';
import { CONSTRAINT_TYPES } from 'dpl/shared/validations/constraints';
import { sendAnalyticsEvent, sendRudderstackEvent } from 'dpl/util/analytics';

import {
  breederProfileDataQueryDefinition,
  createPaymentRequestMutationDefinition,
  updatePaymentRequestMutationDefinition,
  paymentRequestCategoryOptionsQueryDefinition
} from '../utils/queryDefinitions';
import PayNowOrLaterToggle from '../../PayNowOrLaterToggle';
import PayLaterForm from '../../PayLaterForm';
import Summary from '../../Summary';

const MAX_PAYMENT_AMOUNT_IN_DOLLARS = 99999;

const MODAL_STEPS = {
  ACCOUNT_DATA: 'ACCOUNT_DATA',
  PAYMENT: 'PAYMENT'
};

const INITIAL_STATE = {
  breederProfile: {},
  paymentCategories: [],
  paymentRequest: {},
  puppy: {},
  paymentIntent: {},
  klarnaEnabled: false
};

function PaymentRequestModalV2() {
  const {
    isOffPlatform,
    category: initialCategory,
    amount: initialAmount,
    breederProfileId
  } = useSelector(reduxState => ({
    category: reduxState.queryParams[PAYMENT_REQUEST_CATEGORY],
    amount: Number(reduxState.queryParams[PAYMENT_REQUEST_AMOUNT]) || 0,
    breederProfileId: reduxState.server.breederProfileId,
    isOffPlatform: reduxState.server.isOffPlatform
  }));

  const [state, setState] = useState(INITIAL_STATE);
  const [stripeFormData, setStripeFormData] = useState({});
  const checkoutSessionId = useRef(uuidv4());

  const prefilledAmount =
    initialAmount > MAX_PAYMENT_AMOUNT_IN_DOLLARS ? 0 : initialAmount;

  const {
    breederProfile,
    paymentCategories,
    paymentRequest,
    paymentIntent,
    puppy
  } = state;

  const [setupIntent, setSetupIntent] = useState({});
  const currentUser = useCurrentUser();

  const {
    user_id: userId,
    user_name: userName,
    email: userEmail,
    claimed: accountClaimed,
    buyer_profile_id: buyerProfileId
  } = currentUser.value?.data ?? {};

  const [currentStep, setCurrentStep] = useState(
    accountClaimed ? MODAL_STEPS.PAYMENT : MODAL_STEPS.ACCOUNT_DATA
  );

  const {
    mutateAsync: createPaymentRequest,
    isLoading: isCreatingPaymentRequest
  } = useResourceMutation(
    createPaymentRequestMutationDefinition(buyerProfileId)
  );

  const { mutateAsync: createSetupIntent, isLoading: isCreatingPaymentIntent } =
    useResourceMutation(createSetupIntentMutationDefinition);

  const {
    mutateAsync: updatePaymentRequest,
    isLoading: isUpdatingPaymentRequest
  } = useResourceMutation(
    updatePaymentRequestMutationDefinition(
      buyerProfileId,
      paymentRequest.external_id
    )
  );

  const { value: breederProfileData } = useResourceQuery({
    ...breederProfileDataQueryDefinition(breederProfileId),
    enabled: isEmpty(breederProfile) && (!userId || !isCreatingPaymentRequest)
  });

  const {
    value: { data: paymentCategoriesData }
  } = useResourceQuery({
    ...paymentRequestCategoryOptionsQueryDefinition,
    enabled:
      isEmpty(paymentCategories) && (!userId || !isCreatingPaymentRequest)
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isEditingMessage, setIsEditingMessage] = useState(false);

  const debouncedIsEditing = useCallback(debounce(setIsEditing, 250), []);

  const setupIntentClientSecret = setupIntent.client_secret;

  const {
    addToFormState,
    batchSetValue,
    contextValue,
    formState,
    handleFormFieldChange
  } = useForm({
    defaultFormState: {
      ...paymentRequest,
      pay_now: true,
      breeder_profile_id: breederProfileId,
      payment_tier: PAYMENT_TIER_TYPE.PROTECTED,
      amount_in_dollars:
        paymentRequest.amount_in_cents / 100 || prefilledAmount,
      category: Object.values(PAYMENT_REQUEST_CATEGORIES).includes(
        initialCategory
      )
        ? initialCategory
        : '',
      personal_message: ''
    },
    mapFormStateToValidationSchema: () => ({
      amount_in_dollars: CONSTRAINT_TYPES.NUMBER,
      category: CONSTRAINT_TYPES.NON_EMPTY
    })
  });

  async function updatePaymentRequestData(body) {
    const { data } = await updatePaymentRequest({ body });

    if (!isEditing) {
      setState({
        ...state,
        paymentRequest: data.payment_request,
        paymentIntent: data.payment_intent,
        puppy: data.dog,
        breederProfile: data.breeder_profile,
        paymentCategories: data.available_values.payment_categories
      });
    }

    return data;
  }

  function handleFormChange(e) {
    setIsEditingMessage(e.target.name === 'personal_message');
    setIsEditing(true);
    handleFormFieldChange(e);
    debouncedIsEditing(false);
  }

  const hasValidData = Boolean(
    formState.breeder_profile_id &&
      formState.payment_tier &&
      Number(formState.amount_in_dollars) &&
      formState.category
  );

  async function createInitialPaymentRequest() {
    const { data } = await createPaymentRequest({
      body: {
        payment_request: {
          ...formState,
          amount_in_cents: formState.amount_in_dollars * 100,
          breeder_profile_id: breederProfileId
        }
      }
    });

    setState({
      ...state,
      paymentRequest: {
        ...data.payment_request,
        amount_in_cents: formState.amount_in_cents,
        amount_in_dollars: formState.amount_in_dollars,
        category: formState.category
      },
      klarnaEnabled: data.klarna_enabled,
      paymentIntent: data.payment_intent,
      puppy: data.dog,
      breederProfile: data.breeder_profile,
      paymentCategories: data.available_values.payment_categories
    });

    return data;
  }

  async function goToPaymentStep() {
    trackPaymentEvent({
      paymentRequest: {
        category: formState.category,
        amount: formState.amount_in_dollars,
        initiated_by_buyer: true
      },
      userJourney: ANALYTICS_USER_JOURNEYS.PAYMENTS_BIP,
      buttonCta: 'continue',
      view: ANALYTICS_VIEWS.INPUT_PAYMENT_DETAILS_PAGE,
      viewFeature: ANALYTICS_VIEW_FEATURES.BUYER_INITIATED_PAYMENT,
      viewFeatureLocation:
        ANALYTICS_VIEW_FEATURE_LOCATIONS.BUYER_INITIATED_PAYMENT
    });

    setCurrentStep(MODAL_STEPS.PAYMENT);
  }

  useEffect(() => {
    if (
      currentStep === MODAL_STEPS.PAYMENT &&
      paymentRequest?.id &&
      breederProfileId &&
      buyerProfileId
    ) {
      sendRudderstackEvent(ANALYTICS_EVENTS.PAYMENT_CHECKOUT_STARTED_BY_BUYER, {
        view: ANALYTICS_VIEWS.PAYMENT_DETAILS,
        view_feature: ANALYTICS_VIEW_FEATURES.PAYMENT_CHECKOUT_PAGE,
        payment_request_id: paymentRequest.id,
        breeder_profile_id: breederProfileId,
        buyer_profile_id: buyerProfileId,
        payment_type: paymentRequest.category,
        checkout_session_id: checkoutSessionId.current
      });
    }
  }, [currentStep, paymentRequest?.id, breederProfileId, buyerProfileId]);

  function shouldSyncFormState() {
    return (
      !isEmpty(paymentRequest) &&
      Number(paymentRequest.amount_in_dollars) ===
        Number(formState.amount_in_dollars) &&
      (paymentRequest.category !== formState.category ||
        paymentRequest.payment_tier !== formState.payment_tier)
    );
  }

  function handleChangeTierSelection(tier) {
    if (tier !== formState.payment_tier) {
      setIsEditingMessage(false);
      addToFormState('payment_tier', tier);
    }
  }

  function handlePaymentFormChange(stripeEvent) {
    const paymentMethod = stripeEvent.value.type;

    if (paymentMethod !== formState.payment_method) {
      setIsEditingMessage(false);
      addToFormState('payment_method', paymentMethod);
    }

    setStripeFormData(stripeEvent);
  }

  useEffect(() => {
    if (shouldSyncFormState()) {
      setIsEditingMessage(false);
      batchSetValue({
        category: paymentRequest.category,
        amount_in_dollars: paymentRequest.amount_in_cents / 100,
        payment_tier: paymentRequest.payment_tier
      });
    }
  }, [paymentRequest]);

  useEffect(async () => {
    if (userId) {
      const { data } = await createSetupIntent();
      setSetupIntent(data.setup_intent);
    }
  }, [userId]);

  useEffect(() => {
    scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [currentStep]);

  useEffect(() => {
    if (
      isEmpty(breederProfile) &&
      Number(breederProfileData.id) === Number(breederProfileId)
    ) {
      setState({ ...state, breederProfile: breederProfileData });
    }
  }, [breederProfile, breederProfileData]);

  useEffect(() => {
    if (
      isEmpty(breederProfile) &&
      Number(breederProfileData.id) === Number(breederProfileId)
    ) {
      setState({ ...state, breederProfile: breederProfileData });
    }

    if (isEmpty(paymentCategories) && !isEmpty(paymentCategoriesData)) {
      setState({ ...state, paymentCategories: paymentCategoriesData });
    }
  }, [paymentCategories, paymentCategoriesData]);

  useEffect(() => {
    if (hasValidData && !isEditing) {
      if (isEmpty(paymentRequest)) {
        createInitialPaymentRequest();
      } else {
        updatePaymentRequestData({
          payment_request: {
            ...formState,
            amount_in_cents: formState.amount_in_dollars * 100,
            breeder_profile_id: breederProfileId
          }
        });
      }
    }
  }, [formState, breederProfileId, isEditing]);

  const isUpdating = isEditing || isUpdatingPaymentRequest;

  const isLoadingSummary =
    (isUpdating && !isEditingMessage) || isEmpty(paymentRequest);

  const isStripeFormIncomplete =
    isLoadingSummary ||
    !hasValidData ||
    (stripeFormData.elementType === 'payment' && !stripeFormData.complete);

  if (isCreatingPaymentIntent || (!hasValidData && isCreatingPaymentRequest)) {
    return <LoadingSpinner />;
  }

  return (
    <FormContext.Provider value={contextValue}>
      <div
        className="StripeChargeModalV2 relative"
        data-test-id="payment-request-modal"
      >
        <div className="StripeChargeModalV2__container center ph5 ph0-md">
          <div className="flex-auto mb20-lg mb10-sm center">
            {currentStep === MODAL_STEPS.ACCOUNT_DATA && (
              <>
                <div className="flex flex-column items-center">
                  <SmartImage
                    className="br-100 mb6 bg-lightest-gray"
                    src={breederProfile.profile_photo_url}
                    alt={breederProfile.name}
                    height="80px"
                    width="80px"
                  />
                  <h2 className="f5 f6-md lh-title mb8 tc">
                    Payment for a puppy
                  </h2>
                </div>
                <StripeChargeModalAccountDataStepContainer
                  onSubmitSuccess={goToPaymentStep}
                />
              </>
            )}
            {currentStep === MODAL_STEPS.PAYMENT && (
              <>
                <h2 className="f5 f6-md lh-title mb8 tc tl-ns">
                  {breederProfile.name}
                </h2>
                <div className="br12 bg-sand pt8 pb4 pb8-md ph4 ph8-md">
                  <PaymentRequestAmountQuestionV2
                    breederProfile={breederProfile}
                    puppy={puppy}
                    amountInDollars={formState.amount_in_dollars}
                    autoFocus
                    availableCategories={paymentCategories}
                    category={formState.category}
                    onChange={handleFormChange}
                    shouldShowEditButton={Boolean(prefilledAmount)}
                    isOffPlatform={isOffPlatform}
                    maxAmount={MAX_PAYMENT_AMOUNT_IN_DOLLARS}
                  />
                </div>
                {paymentRequest?.id && (
                  <div
                    className={classnames({
                      'o-10': isUpdating && !isEditingMessage
                    })}
                  >
                    {formState.category ===
                      PAYMENT_REQUEST_CATEGORIES.DOG_PAYMENT &&
                      state.klarnaEnabled && (
                        <PayNowOrLaterToggle
                          analytics={{
                            view: ANALYTICS_VIEWS.PAYMENT_DETAILS,
                            viewFeature:
                              ANALYTICS_VIEW_FEATURES.PAYMENT_CHECKOUT_PAGE,
                            checkoutSessionIdRef: checkoutSessionId
                          }}
                        />
                      )}
                    {(formState.pay_now ||
                      formState.category !==
                        PAYMENT_REQUEST_CATEGORIES.DOG_PAYMENT) && (
                      <LoadingWrapper
                        isLoading={!setupIntentClientSecret}
                        className="transform-center-all"
                      >
                        <CompletePaymentRequestFormV3
                          className="mt6"
                          checkoutSessionIdRef={checkoutSessionId}
                          clientSecret={setupIntentClientSecret}
                          externalId={paymentRequest.external_id}
                          id={paymentRequest.id}
                          breederProfileId={breederProfileId}
                          buyerProfileId={buyerProfileId}
                          paymentCategory={formState.category}
                          paymentAmount={formState.amount_in_dollars}
                          isFetching={isUpdating}
                          disabled={isStripeFormIncomplete}
                          onChange={handlePaymentFormChange}
                          paymentMethod={formState.payment_method}
                          onSubmit={() => {
                            sendAnalyticsEvent(
                              ANALYTICS_EVENTS.BUTTON_CLICKED,
                              {
                                ...createPaymentProperties(
                                  {
                                    ...paymentRequest,
                                    category: formState.category,
                                    amount: formState.amount_in_dollars,
                                    initiated_by_buyer: true,
                                    payment_method: formState.payment_method
                                  },
                                  paymentRequest
                                ),
                                button_cta: SUBMIT_PAYMENT_LABEL,
                                user_journey:
                                  ANALYTICS_USER_JOURNEYS.PAYMENTS_BIP,
                                view: ANALYTICS_VIEWS.PAYMENT_DETAILS,
                                view_feature:
                                  ANALYTICS_VIEW_FEATURES.PAYMENT_CHECKOUT_PAGE,
                                view_feature_location:
                                  ANALYTICS_VIEW_FEATURE_LOCATIONS.PAYMENT_CHECKOUT_PAGE,
                                payment_request_id: paymentRequest.id
                              }
                            );
                          }}
                        >
                          <Summary
                            paymentRequest={paymentRequest}
                            breederProfile={breederProfile}
                            isLoadingSummary={isLoadingSummary}
                            handleChangeTierSelection={
                              handleChangeTierSelection
                            }
                          />
                        </CompletePaymentRequestFormV3>
                      </LoadingWrapper>
                    )}
                    {!formState.pay_now && (
                      <LoadingWrapper
                        isLoading={
                          isCreatingPaymentRequest ||
                          isUpdatingPaymentRequest ||
                          !paymentIntent?.client_secret
                        }
                      >
                        <PayLaterForm
                          clientSecret={paymentIntent.client_secret}
                          externalId={paymentRequest.external_id}
                          checkoutSessionIdRef={checkoutSessionId}
                          paymentRequestId={paymentRequest.id}
                          breederProfileId={breederProfile.id}
                          buyerProfileId={buyerProfileId}
                          paymentCategory={paymentRequest.category}
                          billingDetails={{
                            name: userName,
                            email: userEmail
                          }}
                        >
                          <Summary
                            paymentRequest={paymentRequest}
                            breederProfile={breederProfile}
                            isLoadingSummary={isLoadingSummary}
                            handleChangeTierSelection={
                              handleChangeTierSelection
                            }
                          />
                        </PayLaterForm>
                      </LoadingWrapper>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </FormContext.Provider>
  );
}

export default PaymentRequestModalV2;
